import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  Grid,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import {
  CollectionPage,
  CollectionType,
  createCollection,
  getCollectionById,
  updateCollection,
} from "../../../../service/BuyerConfiguration/CollectionManagement/collectionService";
import { notifyError, notifySuccess } from "../../../../components/ToastContainer/Toast/Toast";

type Props = {
  onClose: () => void;
  onSaved: () => void;
  mode: "add" | "edit";
  collectionId?: number | null;
};

const AddEditCollection: React.FC<Props> = ({ onClose, onSaved, mode, collectionId }) => {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    title: "",
    description: "",
    image_url: "",
    type: "item" as CollectionType,
    page: "home" as CollectionPage,
    status: false,
  });
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [selectedImageName, setSelectedImageName] = useState<string>("");
  const [selectedImagePreview, setSelectedImagePreview] = useState<string>("");

  useEffect(() => {
    if (mode !== "edit" || !collectionId) return;
    setLoading(true);
    getCollectionById(collectionId)
      .then((res: any) => {
        const data = res?.data ?? res;
        setForm({
          title: data?.title || "",
          description: data?.description || "",
          image_url: data?.image_url || "",
          type: (data?.type || "item") as CollectionType,
          page: (data?.page || "home") as CollectionPage,
          status: Boolean(data?.status),
        });
        setSelectedImage(null);
        setSelectedImageName("");
        setSelectedImagePreview("");
      })
      .catch((error: any) => {
        notifyError(error?.response?.data?.message || "Failed to load collection");
      })
      .finally(() => setLoading(false));
  }, [mode, collectionId]);

  const canSubmit = useMemo(() => form.title.trim().length > 0, [form.title]);

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setLoading(true);
    try {
      const payload = new FormData();
      payload.append("title", form.title.trim());
      payload.append("description", form.description.trim() || "");
      if (mode !== "edit") {
        payload.append("type", form.type);
      }
      payload.append("page", form.page);
      payload.append("status", String(form.status));
      if (selectedImage) {
        payload.append("image", selectedImage);
      } else if (form.image_url.trim()) {
        payload.append("image_url", form.image_url.trim());
      }
      if (mode === "edit" && collectionId) {
        await updateCollection(collectionId, payload);
        notifySuccess("Collection updated successfully");
      } else {
        await createCollection(payload);
        notifySuccess("Collection created successfully");
      }
      onSaved();
      onClose();
    } catch (error: any) {
      notifyError(error?.response?.data?.message || "Failed to save collection");
    } finally {
      setLoading(false);
    }
  };

  const handleSelectImage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    if (!file) return;
    if (!["image/jpeg", "image/jpg", "image/png", "image/webp"].includes(file.type)) {
      notifyError("Only JPG, PNG, and WebP images are allowed");
      return;
    }
    setSelectedImage(file);
    setSelectedImageName(file.name);
    setSelectedImagePreview(URL.createObjectURL(file));
  };

  useEffect(() => {
    return () => {
      if (selectedImagePreview) {
        URL.revokeObjectURL(selectedImagePreview);
      }
    };
  }, [selectedImagePreview]);

  return (
    <Box sx={{ mt: 1 }}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            placeholder="Title"
            value={form.title}
            onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Button
              variant="outlined"
              component="label"
              fullWidth
              startIcon={<CloudUploadOutlinedIcon />}
              sx={{
                justifyContent: "flex-start",
                textTransform: "none",
                borderStyle: "dashed",
                borderWidth: 1.5,
                py: 1.1,
              }}
            >
              {selectedImageName || "Upload Collection Image"}
              <input
                type="file"
                hidden
                accept="image/jpeg,image/jpg,image/png,image/webp"
                onChange={handleSelectImage}
              />
            </Button>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.25,
                p: 1,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1.5,
                backgroundColor: "background.paper",
              }}
            >
              <Box
                sx={{
                  width: 86,
                  height: 60,
                  borderRadius: 1,
                  border: "1px solid",
                  borderColor: "divider",
                  overflow: "hidden",
                  backgroundColor: "action.hover",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                {selectedImagePreview || form.image_url ? (
                  <Box
                    component="img"
                    src={selectedImagePreview || form.image_url}
                    alt="Collection preview"
                    sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                ) : (
                  <ImageOutlinedIcon color="disabled" />
                )}
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="caption" color="text.secondary">
                  Image Preview
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    maxWidth: "100%",
                  }}
                >
                  {selectedImageName || (form.image_url ? "Current image selected" : "No image selected")}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            placeholder="Description"
            value={form.description}
            onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth>
            <Select
              value={form.type}
              disabled={mode === "edit"}
              displayEmpty
              renderValue={(selected) => (selected ? String(selected) : "Type")}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, type: e.target.value as CollectionType }))
              }
            >
              <MenuItem value="item">Item</MenuItem>
              <MenuItem value="store">Store</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth>
            <Select
              value={form.page}
              displayEmpty
              renderValue={(selected) => (selected ? String(selected) : "Page")}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, page: e.target.value as CollectionPage }))
              }
            >
              <MenuItem value="home">Home</MenuItem>
              <MenuItem value="banner">Banner</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth>
            <Select
              value={form.status ? "active" : "inactive"}
              displayEmpty
              renderValue={(selected) =>
                selected ? String(selected).charAt(0).toUpperCase() + String(selected).slice(1) : "Status"
              }
              onChange={(e) =>
                setForm((prev) => ({ ...prev, status: e.target.value === "active" }))
              }
            >
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>
      <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end", gap: 1.5 }}>
        <Button variant="outlined" onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={!canSubmit || loading}>
          {mode === "edit" ? "Update Collection" : "Create Collection"}
        </Button>
      </Box>
    </Box>
  );
};

export default AddEditCollection;
